/* eslint-disable react-hooks/exhaustive-deps */
import { CustomTooltip } from '@AMIEWEB/Common/Tooltips/CustomTooltip';
import { transformExistingLicenseData } from '@AMIEWEB/Order/OrderCreationForm/Components/UnitDetail/LicenseGridHelper';
import { selectOrderRequirementTypes } from '@AMIEWEB/Order/Store/OrderCreation.selectors';
import UnitCollapsible from '@AMIEWEB/Unit/Common/UnitCollapsible';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import SearchIcon from '@mui/icons-material/Search';
import {
  Avatar,
  Box,
  Chip,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  debounce,
  useTheme,
} from '@mui/material';
import { AndOrOperator, AndOrText, CollectionType } from 'app/enums/Common';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { OrderLicenseGrid } from './OrderLicenseGrid';
import { getTransformedLicenses } from '@AMIEWEB/Order/OrderCreationForm/Components/UnitDetail/UnitDetailDataTransformer';
import { selectOrderLicenseOptions } from '@AMIEWEB/NewOrder/Store/Order.selectors';

const useStyles = makeStyles()(theme => ({
  searchField: {
    height: '1%',
    width: '100%',
    borderBottom: `1px solid ${theme.palette.system.lightGrey}`,
  },
  chip: {
    margin: '0 1% 1% 0',
  },
  incompleteChip: {
    margin: '0 1% 1% 0',
    border: '2px dashed #bdbdbd',
    opacity: 0.7,
  },
  blue: {
    color: `${theme.palette.components.avatar.color} !important`,
    backgroundColor: theme.palette.primary.main,
  },

  wrapper: {
    backgroundColor: `${theme.palette.framework.system.white} !important`,
    border: `1px solid ${theme.palette.system.lightGrey}`,
    borderRadius: 5,
    minHeight: 300,
  },
  noBorder: {
    border: 'hidden',
  },
  chipWrapper: {
    paddingTop: '0.8%',
    display: 'none', //hide license chips section
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  chipIcon: {
    fontSize: 'large',
    marginLeft: 6,
  },
  green: {
    color: `${theme.palette.components.avatar.color} !important`,
    backgroundColor: theme.palette.framework.chart.green,
  },
  radioStyles: {
    '&.Mui-checked': {
      color: `${theme.palette.system.main} !important`,
    },
    '&:hover': {
      color: `${theme.palette.system.main} !important`,
      backgroundColor: 'transparent !important',
    },
  },
}));

interface Props {
  division?: string;
  skillSetIndex: number;
  groupIndex: number;
  value: any;
}

export const OrderLicenseGridWrapper = (props: Props) => {
  const theme = useTheme();
  const { skillSetIndex, groupIndex, value: skillSetValue } = props;
  const { t } = useTranslation();
  const { classes } = useStyles();
  const orderLicenseOptions = useSelector(selectOrderLicenseOptions);
  const { states } = useSelector(selectOrderRequirementTypes);
  const { control, setValue, watch } = useFormContext();
  const [licenseRequirements, setLicenseRequirements] = useState<any[] | null>([]);
  const existingSkillsetLicenseData = skillSetValue?.collections[CollectionType.LicensesIndex]?.requirements ?? [];

  const skillsetLicenseData = watch(
    `skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].collections[${CollectionType.LicensesIndex}].requirements`,
  );

  const transformedLicenses = useMemo(() => {
    let resultData = [];

    if (skillsetLicenseData?.length) {
      skillsetLicenseData?.forEach(skill => {
        if (skill?.licenseDescription !== '') {
          resultData.push(skill);
        }
      });
    }
    return resultData;
  }, [skillsetLicenseData]);

  const transformedData = getTransformedLicenses(transformedLicenses);

  useEffect(() => {
    if (orderLicenseOptions?.length) {
      setLicenseRequirements(getSelectionInfo(''));
    }

    if (existingSkillsetLicenseData && skillSetValue && states?.length) {
      loadSavedLicenses(states);
    }
  }, [orderLicenseOptions, states]);

  const getSelectionInfo = searchQuery => {
    if (!Array.isArray(orderLicenseOptions)) return [];
    const searchResult =
      searchQuery && searchQuery.length >= 2
        ? orderLicenseOptions?.filter(item =>
            `${item?.abbreviation} - ${item?.credentialName}`
              .toLowerCase()
              .replace(/\s|\.|\(|\)/g, '')
              .includes(searchQuery.toLowerCase().replace(/\s|\.|\(|\)/g, '')),
          )
        : orderLicenseOptions;

    return searchResult.map(item => ({
      id: item.requirementTypeId,
      abbreviation: item.abbreviation,
      category: item.category,
      credentialName: item.credentialName,
      isSelected: false,
      requirementTypeId: item.requirementTypeId,
      subCategory: item.subCategory,
      compact: item?.compact,
      compactStates: item?.compactStates,
    }));
  };

  const handleSearch = event => {
    (async () => {
      if (Array.isArray(orderLicenseOptions) && orderLicenseOptions?.length > 0) {
        setLicenseRequirements(getSelectionInfo(event.target?.value));
      }
    })();
  };

  const handleDelete = (licenseToDelete, data: any[]) => {
    let selectedLicenseDetails = [];
    const licenseFromData = data?.find(
      licenseDetails =>
        licenseDetails?.licenseId === (licenseToDelete?.licenseId ?? licenseToDelete?.requirementTypeId),
    );

    if (
      (licenseToDelete.compactStatus === 'Single State' || licenseToDelete.isMultiState === false) &&
      licenseFromData.licenseStateDetails?.length > 1
    ) {
      selectedLicenseDetails = data?.map(license => ({
        ...license,
        licenseStateDetails:
          license?.licenseId === (licenseToDelete.licenseId ?? licenseToDelete.requirementTypeId)
            ? license?.licenseStateDetails?.filter(s => s?.state?.value !== licenseToDelete.stateAbbreviation)
            : license?.licenseStateDetails,
      }));
    } else {
      selectedLicenseDetails = data?.filter(
        license => license?.licenseId !== (licenseToDelete.licenseId ?? licenseToDelete.requirementTypeId),
      );
    }
    setValue(
      `skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].collections[${CollectionType.LicensesIndex}].requirements`,
      selectedLicenseDetails,
    );
  };

  const loadSavedLicenses = useCallback(
    allStates => {
      const formattedLicenses = transformExistingLicenseData(
        skillSetValue?.isDuplicate,
        existingSkillsetLicenseData,
        allStates,
      );

      setValue(
        `skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].collections[${CollectionType.LicensesIndex}].requirements`,
        formattedLicenses,
      );
    },
    [skillSetValue?.isDuplicate],
  );

  return (
    <>
      <Grid xs={12}>
        <UnitCollapsible
          title={`Licenses: ${transformedLicenses?.length}`}
          showIcon={false}
          isCollapsible={transformedLicenses?.length > 0}
          fieldCount={transformedLicenses?.length}
          children={
            <>
              {transformedLicenses?.length > 0 && (
                <Controller
                  name={`skillSetGroup.groups[${groupIndex}].skillsets.${skillSetIndex}.collections[${CollectionType.LicensesIndex}].operation`}
                  control={control}
                  defaultValue={
                    skillSetValue?.collections[CollectionType.LicensesIndex]?.operation
                      ? skillSetValue?.collections[CollectionType.LicensesIndex]?.operation
                      : AndOrOperator.And
                  }
                  render={({ field }) => (
                    <RadioGroup value={field.value} aria-label="Required" name={'required'} onChange={field.onChange} row>
                      <FormControlLabel
                        value={AndOrOperator.And}
                        control={<Radio className={classes.radioStyles} disableRipple disableFocusRipple />}
                        label={AndOrText.And}
                      />
                      <FormControlLabel
                        value={AndOrOperator.Or}
                        control={<Radio className={classes.radioStyles} disableRipple disableFocusRipple />}
                        label={AndOrText.Or}
                      />
                    </RadioGroup>
                  )}
                />
              )}
              <Box className={classes.wrapper}>
                <TextField
                  variant="outlined"
                  color="primary"
                  placeholder={t('order.createUnit.qualifications.placeHolder')}
                  id="txtLicense"
                  autoComplete="on"
                  onChange={debounce(handleSearch, 300)}
                  fullWidth
                  classes={{ root: classes.searchField }}
                  InputProps={{
                    startAdornment: <SearchIcon style={{ opacity: 0.6 }} />,
                    classes: { notchedOutline: classes.noBorder },
                    autoComplete: 'off'
                  }}
                />
                <OrderLicenseGrid
                  filteredLicenses={licenseRequirements}
                  skillSetIndex={skillSetIndex}
                  groupIndex={groupIndex}
                  orderLicenseOptions= {orderLicenseOptions}
                />
              </Box>
              <div className={classes.chipWrapper} id={'createUnitQualificationsLicenseAddedItemContainer'}>
                {skillsetLicenseData &&
                  transformedData?.map((item, idx) => {
                    const isRequiredText = item?.isRequired
                      ? t('order.createUnit.required')
                      : t('order.createUnit.preferred');
                    const stateValue = item?.requirementState?.state
                      ? item?.requirementState?.state
                      : `${t('order.newOrderCreation.tabs.orderTypeDetail.compact')}`;
                    return (
                      <div key={idx} style={{ paddingRight: '6px' }}>
                        <CustomTooltip
                          tooltipContent={`(${isRequiredText}) ${item?.requirementDescription} - ${stateValue}`}
                          placement="bottom"
                          isStandardContent={true}
                          contentStyle={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap' }}
                        >
                          <Chip
                            variant="outlined"
                            avatar={
                              <Avatar className={` ${item?.isRequired ? classes.blue : classes.green}`}>
                                {item.isRequired ? 'R' : 'P'}
                              </Avatar>
                            }
                            key={item.licenseId}
                            label={
                              <React.Fragment>
                                {item?.isMultiState
                                  ? `${item?.requirementAbbreviation} - ${t(
                                      'order.newOrderCreation.tabs.orderTypeDetail.compact',
                                    )}`
                                  : item?.isMultiState === false && item?.requirementState?.stateAbbreviation
                                  ? `${item?.requirementAbbreviation} - ${item?.requirementState?.stateAbbreviation}`
                                  : `${item?.requirementAbbreviation}`}
                                {item?.documentAttachmentRequired && (
                                  <DescriptionOutlinedIcon
                                    style={{ fill: theme.palette.system.darkGray }}
                                    className={classes.chipIcon}
                                  />
                                )}
                              </React.Fragment>
                            }
                            className={item.isMultiState !== null ? classes.chip : classes.incompleteChip}
                            onDelete={() => handleDelete(item, skillsetLicenseData)}
                          />
                        </CustomTooltip>
                      </div>
                    );
                  })}
              </div>
            </>
          }
        />
      </Grid>
    </>
  );
};
